import React, { FC, memo, useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { TableRenderer } from '@web/components/table';
import { CidCvnTableBody } from './cid-cvn-table-body';
import { CidCvnTableHeaders } from './cid-cvn-table-headers';
import { CidCvnRecord, CidCvnCheckResult } from '@packages/models/api/cid-cvn';
import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';

const styles = StyleSheet.create({
    emptyStateDescription: {
        fontSize: 21,
        fontWeight: 600,
        lineHeight: 32,
    },
    typographyStyle: {
        padding: 0,
    },
});

const EmptyTableStateDescription: FC<{ cidCvnEligible: boolean }> = memo(({ cidCvnEligible }) => {
    const { t } = useTranslation();
    const description = cidCvnEligible
        ? 'cidCvnCheck:table.empty.noChecksPerformed'
        : 'cidCvnCheck:table.empty.unsupported';

    return (
        <Typography color="textDarkSecondary" style={styles.emptyStateDescription}>
            {t(description)}
        </Typography>
    );
});

interface CidCvnTableProps {
    canPerformCidCvnCheck: boolean;
    canViewDetailedResults: boolean;
    canViewTechlineCase: boolean;
    canViewTechlineDraftCase: boolean;
    cidCvnEligible: boolean;
    data?: CidCvnRecord[];
    isLoading: boolean;
    downloadingIds: { [id: string]: boolean };
    retryingIds: { [id: string]: boolean };
    onDownload: (id: string) => Promise<void>;
    onRetry: (id: string) => void;
    onReportNameClick: (id: string, reportName: string, result: CidCvnCheckResult) => void;
    onViewCase: (caseNumber: string, result: CidCvnCheckResult) => void;
}

export function CidCvnTable({
    canPerformCidCvnCheck,
    canViewDetailedResults,
    canViewTechlineCase,
    canViewTechlineDraftCase,
    cidCvnEligible,
    data,
    downloadingIds,
    retryingIds,
    isLoading,
    onDownload,
    onRetry,
    onReportNameClick,
    onViewCase,
}: CidCvnTableProps) {
    const tableRowsData = Array.isArray(data)
        ? data.map((item) => ({
              ...item,
              rowId: item.cidCvnStatusId,
          }))
        : [];

    const tableRowsDataSetter = () => {};
    const tableBodyRowRenderer = (rowData: CidCvnRecord) => (
        <CidCvnTableBody
            canPerformCidCvnCheck={canPerformCidCvnCheck}
            canViewDetailedResults={canViewDetailedResults}
            canViewTechlineCase={canViewTechlineCase}
            canViewTechlineDraftCase={canViewTechlineDraftCase}
            data={rowData}
            isDownloading={!!downloadingIds[rowData.cidCvnStatusId]}
            isRetrying={!!retryingIds[rowData.cidCvnStatusId]}
            key={rowData.cidCvnStatusId}
            onDownload={onDownload}
            onReportNameClick={onReportNameClick}
            onRetry={onRetry}
            onViewCase={onViewCase}
        />
    );
    const renderTableHeaders = useCallback(() => <CidCvnTableHeaders />, []);

    return (
        <TableRenderer
            emptyStateProps={{
                description: <EmptyTableStateDescription cidCvnEligible={cidCvnEligible} />,
                descriptionStyle: styles.typographyStyle,
                icon: cidCvnEligible ? 'ecm' : 'x-circle',
            }}
            isLoading={isLoading}
            tableBodyRowRenderer={tableBodyRowRenderer}
            tableHeaderRowRenderer={renderTableHeaders}
            tableRowsData={tableRowsData}
            tableRowsDataSetter={tableRowsDataSetter}
        />
    );
}
